import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { subscriptionAPI } from "./api";
import { useDispatch } from "react-redux";
import { openModal } from "../features/ui/uiSlice";
import OnboardingRequiredModal from "../components/common/OnboardingRequiredModal";
import { useUserStore } from "../store/useUserStore";
import { useAuth as useCrossmintAuth } from "@crossmint/client-sdk-react-ui";
import { useGetMe } from "@/hooks/useGetMe";
import { UserRole } from "@/types/users.type";

// Neutral blank screen shown while auth/route resolves — no spinner icon
// (the app uses skeletons / blank surfaces instead of loading spinners).
// `text` is accepted for backwards-compat but intentionally not rendered.
export const SimpleLoader = () => (
  <div className="min-h-screen bg-white" aria-hidden="true" />
);

const ProtectedRoute = ({
  children,
  requireOnboarding = false,
  onboardingMode = "redirect",
  requireAdmin = false,
}) => {
  const {
    isAuthenticated: reduxIsAuthenticated,
    isLoading,
    onboardingCompleted: reduxOnboardingCompleted,
    onboardingSkipped: reduxOnboardingSkipped,
    user: reduxUser,
  } = useAuth();

  const { user: crossmintUser } = useCrossmintAuth();

  const storeUser = useUserStore((state) => state.user);
  const storeToken = useUserStore((state) => state.token);
  const storeIsAuthenticated = useUserStore((state) => state.isAuthenticated);
  const storeOnboardingCompleted = useUserStore(
    (state) => state.onboardingCompleted,
  );
  const storeOnboardingSkipped = useUserStore(
    (state) => state.onboardingSkipped,
  );

  const user = reduxUser || storeUser || crossmintUser;
  const isAuthenticated =
    reduxIsAuthenticated || storeIsAuthenticated || Boolean(storeToken);
  const onboardingCompleted =
    reduxOnboardingCompleted || storeOnboardingCompleted;
  const onboardingSkipped = reduxOnboardingSkipped || storeOnboardingSkipped;

  // Fetch /users/me when authenticated (enabled by token in useGetMe)
  useGetMe({ staleTimeMs: 1 * 60 * 1000 });

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  // Check if we have a token on initial load
  const hasToken =
    Boolean(storeToken) ||
    Boolean(localStorage.getItem("token")) ||
    crossmintUser;
  const hasStoredUser =
    Boolean(storeUser) ||
    Boolean(localStorage.getItem("user")) ||
    crossmintUser;

  // Check subscription/trial status when authenticated and onboarding done
  // useEffect(() => {
  //   const checkAccess = async () => {
  //     if (!isAuthenticated || !onboardingCompleted) {
  //       setAccessChecked(true);
  //       return;
  //     }
  //     try {
  //       // 1) Check status
  //       const status = await subscriptionAPI.getSubscriptionStatus();

  //       if (status?.has_access) {
  //         setHasAccess(true);
  //         setAccessChecked(true);
  //         return;
  //       }
  //       // 2) Attempt to start trial if never used
  //       if (
  //         status?.trial &&
  //         !status.trial.active &&
  //         !status.trial.has_used_trial
  //       ) {
  //         const trialRes = await subscriptionAPI.startTrial();
  //         if (trialRes?.status === "started") {
  //           setHasAccess(true);
  //           setAccessChecked(true);
  //           return;
  //         }
  //       }

  //       // 3) No access: prompt subscription
  //       setHasAccess(false);
  //       setAccessChecked(true);
  //       dispatch(openModal("subscription"));
  //     } catch (e) {
  //       // On error, allow access to avoid blocking; optionally log
  //       setHasAccess(true);
  //       setAccessChecked(true);
  //     }
  //   };
  //   checkAccess();
  // }, [isAuthenticated, onboardingCompleted, dispatch]);

  // Show loading only in specific cases to avoid unnecessary loading screens
  if (isLoading && (!hasToken || !hasStoredUser || !user)) {
    return <SimpleLoader text="Checking authentication..." />;
  }

  if (!isAuthenticated && !hasToken) {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    const rootParams = new URLSearchParams();
    if (ref) rootParams.set("ref", ref);
    const rootUrl = rootParams.toString()
      ? `/login?${rootParams.toString()}`
      : "/login";

    return <Navigate to={rootUrl} state={{ from: location }} replace />;
  }

  // If has token and crossmint user data, navigate to the canonical root route
  if (hasToken && crossmintUser && location.pathname === "/login") {
    return <Navigate to="/home" replace />;
  }

  const role = user?.role ?? storeUser?.role ?? reduxUser?.role;
  const roleResolved = role != null;

  // Wait for role before making role-based routing decisions
  if (hasToken && !roleResolved) {
    return <SimpleLoader text="Checking permissions..." />;
  }

  // Admin routes: only ADMIN or SUPER_ADMIN; USER redirect to home (user area)
  if (requireAdmin) {
    const isAdminOrSuperAdmin =
      role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;
    if (!isAdminOrSuperAdmin) {
      return <Navigate to="/home" replace />;
    }
  }

  // If user has no access (subscription/trial expired), redirect to Home
  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  // If user has access, show the protected content
  return children;
};

export default ProtectedRoute;
